import AttachmentIcon from '@atlaskit/icon/core/attachment';
import LinkIcon from '@atlaskit/icon/core/link';
import { FieldTypeCategory, getFieldTypes } from '@databases/fields';
import { messages } from '@databases/intl/messages';
import { DatabaseFieldType } from '@databases/types/database';
import { useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { addFieldMenuMessages } from './messages';
import type { FieldTypeEntry } from './types';

const isFieldTypeVisible = (type: DatabaseFieldType, showTypes?: DatabaseFieldType[]) =>
  !showTypes || showTypes.includes(type);

const basicFieldTypeOrder = [
  DatabaseFieldType.TEXT,
  DatabaseFieldType.HYPERLINK,
  DatabaseFieldType.SELECT,
  DatabaseFieldType.USER,
  DatabaseFieldType.DATE,
  DatabaseFieldType.ATTACHMENT,
  DatabaseFieldType.NUMBER,
  DatabaseFieldType.CHECKBOX
];

const basicFieldTypeOrderM2 = [
  DatabaseFieldType.TEXT,
  DatabaseFieldType.SELECT,
  DatabaseFieldType.DATE,
  DatabaseFieldType.NUMBER,
  DatabaseFieldType.HYPERLINK,
  DatabaseFieldType.USER,
  DatabaseFieldType.ATTACHMENT,
  DatabaseFieldType.CHECKBOX
];

const confluenceFieldTypeOrder = [
  DatabaseFieldType.PAGE_LINK,
  DatabaseFieldType.ENTRY_LINK,
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.BACKLINK,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.PAGE_DETAILS
];

const confluenceFieldTypeOrderM2 = [
  DatabaseFieldType.PAGE_LINK,
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.PAGE_DETAILS,
  DatabaseFieldType.ENTRY_LINK,
  DatabaseFieldType.BACKLINK,
  DatabaseFieldType.ENTRY_DETAILS
];

const jiraFieldTypeOrder = [
  DatabaseFieldType.JIRA_ISSUE,
  DatabaseFieldType.JIRA_ISSUE_DETAILS
];

const basicFieldsIconOverrides: Partial<
  Record<DatabaseFieldType, FieldTypeEntry['Icon']>
> = {
  [DatabaseFieldType.ATTACHMENT]: AttachmentIcon
};

const confluenceIconOverrides: Partial<
  Record<DatabaseFieldType, FieldTypeEntry['Icon']>
> = {
  [DatabaseFieldType.PAGE_LINK]: LinkIcon
};

const jiraIconOverrides: Partial<Record<DatabaseFieldType, FieldTypeEntry['Icon']>> = {
  [DatabaseFieldType.JIRA_ISSUE]: LinkIcon
};

const tooltipMessages: Partial<Record<DatabaseFieldType, MessageDescriptor>> = {
  [DatabaseFieldType.TEXT]: addFieldMenuMessages.textTooltip,
  [DatabaseFieldType.ATTACHMENT]: addFieldMenuMessages.mediaAndFilesTooltip,
  [DatabaseFieldType.NUMBER]: addFieldMenuMessages.numberTooltip,
  [DatabaseFieldType.DATE]: addFieldMenuMessages.dateTooltip,
  [DatabaseFieldType.SELECT]: addFieldMenuMessages.tagTooltip,
  [DatabaseFieldType.USER]: addFieldMenuMessages.userTooltip,
  [DatabaseFieldType.HYPERLINK]: addFieldMenuMessages.urlTooltip,
  [DatabaseFieldType.CHECKBOX]: addFieldMenuMessages.checkboxTooltip,
  [DatabaseFieldType.PAGE_LINK]: addFieldMenuMessages.confluenceLinkTooltip,
  [DatabaseFieldType.PAGE_STATUS]: addFieldMenuMessages.confluenceStatusTooltip,
  [DatabaseFieldType.PAGE_LABELS]: addFieldMenuMessages.confluenceLabelTooltip,
  [DatabaseFieldType.PAGE_DETAILS]: addFieldMenuMessages.confluenceMoreDetailsTooltip,
  [DatabaseFieldType.ENTRY_LINK]: addFieldMenuMessages.entryLinkTooltip,
  [DatabaseFieldType.BACKLINK]: addFieldMenuMessages.entryBacklinkTooltip,
  [DatabaseFieldType.ENTRY_DETAILS]: addFieldMenuMessages.entryDetailTooltip,
  [DatabaseFieldType.JIRA_ISSUE]: addFieldMenuMessages.jiraLinkTooltip,
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: addFieldMenuMessages.jiraDetailTooltip
};

type FieldTypeEntriesVariant = 'add-field-menu' | 'contextual-menu';

export const useFieldTypeEntries = (
  showTypes: DatabaseFieldType[] | undefined,
  variant: FieldTypeEntriesVariant = 'add-field-menu'
) => {
  const { formatMessage } = useIntl();
  const isM2Enabled =
    variant === 'contextual-menu' &&
    getExperimentValue('databasesContextualMenusM2', 'enabled', {
      fireExperimentExposure: false
    });
  const isCheckboxEnabled = getExperimentValue('checkboxFieldType', 'enabled', {
    fireExperimentExposure: false
  });

  const basicLabelOverrides = useMemo<Partial<Record<DatabaseFieldType, string>>>(
    () => ({
      [DatabaseFieldType.HYPERLINK]: formatMessage(addFieldMenuMessages.link)
    }),
    [formatMessage]
  );

  const confluenceLabelOverrides = useMemo<Partial<Record<DatabaseFieldType, string>>>(
    () => ({
      [DatabaseFieldType.PAGE_LINK]: formatMessage(addFieldMenuMessages.confluenceLink),
      [DatabaseFieldType.PAGE_DETAILS]: formatMessage(
        addFieldMenuMessages.confluenceMoreDetails
      ),
      [DatabaseFieldType.ENTRY_DETAILS]: formatMessage(addFieldMenuMessages.entryDetail)
    }),
    [formatMessage]
  );

  const jiraLabelOverrides = useMemo<Partial<Record<DatabaseFieldType, string>>>(
    () => ({
      [DatabaseFieldType.JIRA_ISSUE]: formatMessage(addFieldMenuMessages.jiraLink),
      [DatabaseFieldType.JIRA_ISSUE_DETAILS]: formatMessage(
        addFieldMenuMessages.jiraDetail
      )
    }),
    [formatMessage]
  );

  return useMemo(() => {
    const fieldTypes = getFieldTypes();
    const fieldTypeEntries = Object.entries(fieldTypes) as Array<
      [DatabaseFieldType, (typeof fieldTypes)[DatabaseFieldType]]
    >;

    const entries = fieldTypeEntries
      .filter(
        ([type]) =>
          isFieldTypeVisible(type, showTypes) &&
          (type !== DatabaseFieldType.CHECKBOX || isCheckboxEnabled)
      )
      .map(([type, fieldType]) => ({
        type,
        label:
          basicLabelOverrides[type] ??
          confluenceLabelOverrides[type] ??
          jiraLabelOverrides[type] ??
          formatMessage(fieldType.label),
        labelDescriptor: fieldType.label,
        Icon:
          basicFieldsIconOverrides[type] ??
          confluenceIconOverrides[type] ??
          jiraIconOverrides[type],
        category: fieldType.category,
        tooltip: tooltipMessages[type] ? formatMessage(tooltipMessages[type]) : undefined
      }));

    const basicEntries = entries.filter(
      ({ category }) => category === FieldTypeCategory.BASIC
    );

    return {
      basic: (isM2Enabled ? basicFieldTypeOrderM2 : basicFieldTypeOrder).flatMap((type) =>
        basicEntries.filter((entry) => entry.type === type)
      ),
      confluence: (isM2Enabled ? confluenceFieldTypeOrderM2 : confluenceFieldTypeOrder)
        .flatMap((type) => entries.filter((entry) => entry.type === type))
        .filter(
          ({ category }) =>
            category === FieldTypeCategory.PAGE ||
            category === FieldTypeCategory.OTHER_DATABASES
        ),
      jira: jiraFieldTypeOrder
        .flatMap((type) => entries.filter((entry) => entry.type === type))
        .filter(({ category }) => category === FieldTypeCategory.JIRA),
      jiraLabel: formatMessage(messages['field-type.category-jira']),
      basicLabel: formatMessage(addFieldMenuMessages.basicFieldTypesSection),
      confluenceLabel: formatMessage(addFieldMenuMessages.confluenceSection)
    };
  }, [
    basicLabelOverrides,
    confluenceLabelOverrides,
    formatMessage,
    isCheckboxEnabled,
    isM2Enabled,
    jiraLabelOverrides,
    showTypes
  ]);
};
